import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Thermometer, Scale, TrendingUp, TrendingDown, Minus } from 'lucide-react';

// Simple SVG Sparkline component
function Sparkline({ data, color = '#10b981', height = 40, width = 120 }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-10 text-gray-400 text-xs">
        Sem dados
      </div>
    );
  }

  const values = data.map(d => d.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  
  const padding = 4;
  const chartWidth = width - padding * 2;
  const chartHeight = height - padding * 2;
  
  // Generate path points
  const points = values.map((value, index) => {
    const x = padding + (index / (values.length - 1 || 1)) * chartWidth;
    const y = padding + chartHeight - ((value - min) / range) * chartHeight;
    return `${x},${y}`;
  });
  
  const pathD = `M ${points.join(' L ')}`;
  
  // Create area path for fill
  const areaPoints = [
    `${padding},${height - padding}`,
    ...points,
    `${width - padding},${height - padding}`
  ];
  const areaD = `M ${areaPoints.join(' L ')} Z`;
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      {/* Gradient fill */}
      <defs>
        <linearGradient id={`gradient-${color.replace('#', '')}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
      </defs>
      
      {/* Area fill */}
      <path 
        d={areaD} 
        fill={`url(#gradient-${color.replace('#', '')})`}
      />
      
      {/* Line */}
      <path 
        d={pathD} 
        fill="none" 
        stroke={color} 
        strokeWidth="2" 
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      {/* Last point dot */}
      {points.length > 0 && (
        <circle 
          cx={width - padding} 
          cy={padding + chartHeight - ((values[values.length - 1] - min) / range) * chartHeight}
          r="3" 
          fill={color}
        />
      )}
    </svg>
  );
}

function TrendIcon({ trend, className = "" }) {
  switch (trend) {
    case 'up':
      return <TrendingUp className={`w-4 h-4 ${className}`} />;
    case 'down':
      return <TrendingDown className={`w-4 h-4 ${className}`} />;
    default:
      return <Minus className={`w-4 h-4 ${className}`} />;
  }
}

export default function VitalTrendsPanel({ animalId }) {
  const [loading, setLoading] = useState(true);
  const [trends, setTrends] = useState(null);

  useEffect(() => {
    if (animalId) {
      fetchTrends();
    }
  }, [animalId]);

  const fetchTrends = async () => {
    try {
      const response = await axios.get(`${API}/animals/${animalId}/vital-trends?days=30`);
      setTrends(response.data);
    } catch (error) {
      console.error('Error fetching vital trends:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="animate-pulse">
          <CardContent className="p-4">
            <div className="h-20 bg-gray-200 rounded"></div>
          </CardContent>
        </Card>
        <Card className="animate-pulse">
          <CardContent className="p-4">
            <div className="h-20 bg-gray-200 rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!trends) {
    return null;
  }

  const { temperature, weight } = trends;

  const getTrendColor = (trend) => {
    switch (trend) {
      case 'up': return 'text-red-500';
      case 'down': return 'text-blue-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vital-trends-panel">
      {/* Temperature Card */}
      <Card className="border-orange-100 bg-gradient-to-br from-orange-50 to-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Thermometer className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Temperatura</h4>
                <p className="text-xs text-gray-500">Últimos 30 dias</p>
              </div>
            </div>
            {temperature.latest && (
              <div className="text-right">
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-bold text-orange-600">{temperature.latest}°C</span>
                  <TrendIcon trend={temperature.trend} className={getTrendColor(temperature.trend)} />
                </div>
              </div>
            )}
          </div>
          
          <Sparkline 
            data={temperature.data} 
            color="#f97316" 
            height={50} 
            width={200}
          />
          
          {temperature.data.length > 0 && (
            <div className="flex justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-orange-100">
              <span>Mín: {temperature.min}°C</span>
              <span>Média: {temperature.avg}°C</span>
              <span>Máx: {temperature.max}°C</span>
            </div>
          )}
          
          {temperature.data.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-4">
              Nenhum registro de temperatura
            </p>
          )}
        </CardContent>
      </Card>

      {/* Weight Card */}
      <Card className="border-emerald-100 bg-gradient-to-br from-emerald-50 to-white">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Scale className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-800">Peso</h4>
                <p className="text-xs text-gray-500">Últimos 30 dias</p>
              </div>
            </div>
            {weight.latest && (
              <div className="text-right">
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-bold text-emerald-600">{weight.latest} kg</span>
                  <TrendIcon trend={weight.trend} className={getTrendColor(weight.trend)} />
                </div>
                <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">
                  {weight.trend === 'up' ? 'Ganho' : weight.trend === 'down' ? 'Perda' : 'Estável'}
                </Badge>
              </div>
            )}
          </div>
          
          <Sparkline 
            data={weight.data} 
            color="#10b981" 
            height={50} 
            width={200}
          />
          
          {weight.data.length > 0 && (
            <div className="flex justify-between text-xs text-gray-500 mt-2 pt-2 border-t border-emerald-100">
              <span>Mín: {weight.min} kg</span>
              <span>Média: {weight.avg} kg</span>
              <span>Máx: {weight.max} kg</span>
            </div>
          )}
          
          {weight.data.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-4">
              Nenhum registro de peso
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
